import React, { useState, useEffect, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import diceImage from '../assets/dice.png';
import { WalletContext } from '../context/WalletContext.js';
import { 
  Connection, 
  PublicKey, 
  LAMPORTS_PER_SOL, 
  ComputeBudgetProgram,
  SYSVAR_RECENT_BLOCKHASHES_PUBKEY,
  SystemProgram, 
  TransactionInstruction, 
  Transaction 
} from '@solana/web3.js';
import BN from 'bn.js';
import { Buffer } from 'buffer';

const DisclaimerModal = ({ onClose }) => {
  return createPortal(
    <div className="modal-overlay">
      <div className="modal-content disclaimer-modal-content">
        <h2>Welcome to SolDice!</h2>
        <p>⚠️ Important: This project is currently only working on the Solana devnet. Please make sure you are connected to the devnet before placing any bets. ⚠️</p>
        <button onClick={onClose}>I understand</button>
      </div>
    </div>,
    document.body
  );
};

const InfoModal = ({ onClose }) => {
  return createPortal(
    <div className="modal-overlay">
      <div className="modal-content info-modal-content">
        <h2>Betting Information</h2>
        <p>Welcome to SolDice, the exciting dice game on the Solana blockchain!</p>
        <h3>Bet Types:</h3>
        <ul>
          <li><strong>Over/Under:</strong> Predict if the roll will be over or under your chosen number.</li>
          <li><strong>Odd/Even:</strong> Bet on whether the roll will be an odd or even number.</li>
        </ul>
        <h3>How it Works:</h3>
        <p>Our smart contract uses Solana's recent blockhashes to generate truly random numbers, ensuring fair play for all participants.</p>
        <h3>Payouts:</h3>
        <p>Payouts are calculated based on the probability of winning. The harder the bet, the higher the potential payout!</p>
        <h3>House Edge:</h3>
        <p>A small 5% house edge is applied to ensure the long-term sustainability of the game.</p>
        <button onClick={onClose}>Close</button>
      </div>
    </div>,
    document.body
  );
};

const DiceRoller = () => {
  const [displayNumber, setDisplayNumber] = useState(50);
  const [isRolling, setIsRolling] = useState(false);
  const [betAmount, setBetAmount] = useState(0.01);
  const [prediction, setPrediction] = useState(50);
  const [betType, setBetType] = useState('over');
  const [result, setResult] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { 
    publicKey, 
    balance, 
    updateBalance,
    houseWalletBalance,
    fetchLeaderboardAndBets
  } = useContext(WalletContext);

  const HOUSE_WALLET = process.env.REACT_APP_HOUSE_WALLET_ADDRESS 
    ? new PublicKey(process.env.REACT_APP_HOUSE_WALLET_ADDRESS)
    : null;

  const connection = new Connection('https://api.devnet.solana.com', 'confirmed');

  const PROGRAM_ID = new PublicKey('BKvtAsFG9uHxHP3Xh9BbQ7sc928Xy7a3NwpzJKgoehTm');

  useEffect(() => {
    const hasSeenDisclaimer = localStorage.getItem('hasSeenDisclaimer');
    if (hasSeenDisclaimer) {
      setShowDisclaimer(false);
    }
  }, []);

  const closeDisclaimer = () => {
    setShowDisclaimer(false);
    localStorage.setItem('hasSeenDisclaimer', 'true');
  };

  const toggleInfoModal = () => {
    setShowInfoModal(!showInfoModal);
  };

  const getBetTypeValue = useCallback((betType) => {
    switch (betType) {
      case 'over':
        return 0;
      case 'under':
        return 1;
      case 'odd':
        return 2;
      case 'even':
        return 3;
      default:
        throw new Error('Invalid bet type');
    }
  }, []);

  const calculatePayout = useCallback((bet) => {
    const amount = bet.amount.toNumber();
    let payout;
    switch (bet.bet_type) {
      case 0: // Over
        payout = Math.floor(amount * (100 / (100 - bet.prediction)));
        break;
      case 1: // Under
        payout = Math.floor(amount * (100 / bet.prediction));
        break;
      case 2: // Odd
      case 3: // Even
        payout = amount * 2;
        break;
      default:
        payout = 0;
    }
    return Number.isFinite(payout) ? payout : 0;
  }, []);

  const handleRoll = async () => {
    if (!publicKey) {
      setTransactionStatus("Error: Wallet not connected. Please connect your wallet.");
      return;
    }
    
    if (!HOUSE_WALLET) {
      setTransactionStatus("Error: House wallet not set. Please check your configuration.");
      return;
    }

    setIsRolling(true);
    setIsLoading(true);
    setResult(null);
    setTransactionStatus("Preparing bet...");

    try {
      const playerPubkey = publicKey;
      const housePubkey = HOUSE_WALLET;

      const bet = {
        amount: new BN(Math.floor(betAmount * LAMPORTS_PER_SOL)),
        bet_type: getBetTypeValue(betType),
        prediction: prediction,
      };

      // Validate bet amount and prediction
      if (bet.amount.toNumber() === 0) {
        throw new Error("Bet amount must be greater than zero");
      }
      if (bet.prediction > 100 || bet.prediction < 1) {
        throw new Error("Prediction must be between 1 and 100");
      }

      // Check if player has enough balance
      if (balance < betAmount) {
        throw new Error("Insufficient balance to place this bet");
      }

      // Create the bet instruction
      const betInstruction = new TransactionInstruction({
        keys: [
          { pubkey: playerPubkey, isSigner: true, isWritable: true },
          { pubkey: housePubkey, isSigner: false, isWritable: true },
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
          { pubkey: SYSVAR_RECENT_BLOCKHASHES_PUBKEY, isSigner: false, isWritable: false },
        ],
        programId: PROGRAM_ID,
        data: Buffer.from([
          1, // Instruction index for PlaceBet
          ...bet.amount.toArray('le', 8),
          bet.bet_type,
          bet.prediction,
        ])
      });

      const transaction = new Transaction();

      // Add compute budget instruction
      const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({ 
        units: 300000 
      });
      transaction.add(modifyComputeUnits);

      // Add priority fee instruction
      const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({ 
        microLamports: 1 
      });
      transaction.add(addPriorityFee);

      // Add the bet instruction
      transaction.add(betInstruction);

      // Set the fee payer
      transaction.feePayer = playerPubkey;

      setTransactionStatus("Getting latest blockhash...");
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
      transaction.recentBlockhash = blockhash;
      transaction.lastValidBlockHeight = lastValidBlockHeight;

      setTransactionStatus("Simulating transaction...");
      try {
        const simulation = await connection.simulateTransaction(transaction);
        if (simulation.value.err) {
          throw new Error(`Transaction simulation failed: ${simulation.value.err}`);
        }
      } catch (error) {
        console.error("Transaction simulation error:", error);
        throw error;
      }

      setTransactionStatus("Requesting wallet signature...");
      const signedTransaction = await window.solana.signTransaction(transaction);

      setTransactionStatus("Sending transaction...");
      const signature = await connection.sendRawTransaction(signedTransaction.serialize());

      setTransactionStatus("Transaction sent. Waiting for confirmation...");
      const confirmation = await connection.confirmTransaction({
        signature,
        lastValidBlockHeight,
        blockhash
      });

      if (confirmation.value.err) {
        throw new Error(`Transaction failed: ${confirmation.value.err}`);
      }
      // Process the result
      const transactionDetails = await connection.getTransaction(signature, { commitment: 'confirmed' });
      const rollResult = extractRollResult(transactionDetails);

      console.log('Extracted roll result:', rollResult);

      if (rollResult !== null) {
        const isWin = isWinningBet(bet, rollResult);
        const payout = isWin ? calculatePayout(bet) : 0;
        
        // Convert profitLoss to SOL
        const betAmountInSol = bet.amount.toNumber() / LAMPORTS_PER_SOL;
        const payoutInSol = payout / LAMPORTS_PER_SOL;
        const profitLoss = isWin ? (payoutInSol - betAmountInSol) : -betAmountInSol;

        setTransactionStatus("Transaction confirmed. Bet completed");
        setResult({
          isWin,
          rollResult,
          betAmount: betAmountInSol,
          profitLoss,
          payout: payoutInSol,
          transactionId: signature,
        });

        updateBalance();

        // Fetch updated leaderboard and bets
        fetchLeaderboardAndBets();

        // Dispatch the 'betMade' event
        window.dispatchEvent(new CustomEvent('betMade'));
      } else {
        throw new Error("Couldn't extract roll result from transaction logs");
      }
    } catch (error) {
      console.error("Error during bet process:", error);
      setTransactionStatus(`Error: ${error.message}`);
    } finally {
      setIsRolling(false);
      setIsLoading(false);
    }
  };

  const extractRollResult = (transaction) => {
    if (transaction && transaction.meta && transaction.meta.logMessages) {
      const rollResultLog = transaction.meta.logMessages.find(log => log.includes("RNG result:"));
      if (rollResultLog) {
        const rollResultMatch = rollResultLog.match(/RNG result: (\d+)/);
        if (rollResultMatch && rollResultMatch[1]) {
          const rollResult = parseInt(rollResultMatch[1]);
          return Number.isFinite(rollResult) ? rollResult : null;
        }
      }
    }
    return null;
  };

  const isWinningBet = (bet, rollResult) => {
    switch (bet.bet_type) {
      case 0: // Over
        return rollResult > bet.prediction;
      case 1: // Under
        return rollResult < bet.prediction;
      case 2: // Odd
        return rollResult % 2 !== 0;
      case 3: // Even
        return rollResult % 2 === 0;
      default:
        return false;
    }
  };

  return (
    <div className="dice-roller-container">
      {showDisclaimer && <DisclaimerModal onClose={closeDisclaimer} />}
      <button className="info-button" onClick={toggleInfoModal}>ℹ️ Betting Info</button>
      {showInfoModal && <InfoModal onClose={toggleInfoModal} />}
      <div className="dice-display">
        <img src={diceImage} alt="Dice" className={`dice-image ${isRolling ? 'spinning' : ''}`}/>
        <div className="dice-number">{displayNumber}</div>
      </div>
      <div className="betting-panel">
        <div className="bet-type-buttons">
          <button onClick={() => setBetType('over')} className={betType === 'over' ? 'active' : ''}>Over</button>
          <button onClick={() => setBetType('under')} className={betType === 'under' ? 'active' : ''}>Under</button>
          <button onClick={() => setBetType('odd')} className={betType === 'odd' ? 'active' : ''}>Odd</button>
          <button onClick={() => setBetType('even')} className={betType === 'even' ? 'active' : ''}>Even</button>
        </div>
        {['over', 'under'].includes(betType) && (
          <div className="input-group">
            <label htmlFor="prediction">Prediction (1-100):</label>
            <input
              type="number"
              id="prediction"
              min="1"
              max="100"
              value={prediction}
              onChange={(e) => setPrediction(parseInt(e.target.value))}
            />
          </div>
        )}
        <div className="input-group">
          <label htmlFor="bet">Bet Amount (SOL):</label>
          <input
            type="number"
            id="bet"
            min="0.0007"
            step="0.01"
            value={betAmount}
            onChange={(e) => setBetAmount(parseFloat(e.target.value))}
          />
        </div>
        <div className="bet-buttons">
          <button onClick={() => setBetAmount((prev) => Math.max(0.01, prev * 0.5))}>0.5x</button>
          <button onClick={() => setBetAmount((prev) => prev * 2)}>2x</button>
          <button onClick={() => setBetAmount(0.01)}>Min (0.01SOL)</button>
        </div>
        <div className="roll-container">
          {isLoading ? (
            <div className="loading">Processing...</div>
          ) : (
            <button className="roll-button" onClick={handleRoll} disabled={!publicKey || isRolling}>
              {publicKey ? (isRolling ? 'Rolling...' : 'Roll Dice') : 'Connect Wallet to Play'}
            </button>
          )}
          <div className="payout-container">
            <p className="payout-text">Payout: {(calculatePayout({amount: new BN(LAMPORTS_PER_SOL), bet_type: getBetTypeValue(betType), prediction: prediction}) / LAMPORTS_PER_SOL).toFixed(2)}x</p>
          </div>
        </div>
      </div>
      {transactionStatus && (
        <div className={`transaction-status ${transactionStatus.includes('Error') || transactionStatus.includes('failed') ? 'error' : ''}`}>
          <pre>{transactionStatus}</pre>
        </div>
      )}
      {result && (
        <div className={`result-message ${result.isWin ? 'win' : 'loss'}`}>
          <h3>{result.isWin ? 'You Won!' : 'You Lost'}</h3>
          <p>Roll Result: {result.rollResult}</p>
          <p>Amount Bet: {result.betAmount.toFixed(2)} SOL</p>
          <p>Profit/Loss: {result.profitLoss.toFixed(4)} SOL</p>
          <p>Payout: {result.payout.toFixed(2)} SOL</p>
          <p>Transaction ID: <a href={`https://explorer.solana.com/tx/${result.transactionId}?cluster=devnet`} target="_blank" rel="noopener noreferrer">{`${result.transactionId.slice(0, 4)}...${result.transactionId.slice(-4)}`}</a></p>
        </div>
      )}
      {houseWalletBalance !== null && (
        <div className="house-balance">
          <p>House Balance: {houseWalletBalance.toFixed(2)} SOL</p>
        </div>
      )}
    </div>
  );
};

export default DiceRoller;